/* ==========================================================================
   Black Mango — Spring Physics Engine
   Lightweight spring simulation for organic, bouncy motion.
   Exposes window.BlackMangoSpring for use by other scripts.
   Respects prefers-reduced-motion.
   ========================================================================== */

(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const IS_TOUCH = 'ontouchstart' in window;

  /* --------------------------------------------------------------------------
     Spring — core physics primitive
     -------------------------------------------------------------------------- */
  class Spring {
    constructor({ stiffness = 100, damping = 10, mass = 1, target = 0 } = {}) {
      this.stiffness = stiffness;
      this.damping = damping;
      this.mass = mass;
      this.value = 0;
      this.velocity = 0;
      this.target = target;
    }

    update(dt) {
      const springForce = -this.stiffness * (this.value - this.target);
      const dampingForce = -this.damping * this.velocity;
      const acceleration = (springForce + dampingForce) / this.mass;
      this.velocity += acceleration * dt;
      this.value += this.velocity * dt;
      return this.value;
    }

    isAtRest(threshold = 0.001) {
      return Math.abs(this.velocity) < threshold &&
             Math.abs(this.value - this.target) < threshold;
    }

    snapTo(val) {
      this.value = val;
      this.target = val;
      this.velocity = 0;
    }
  }

  /* --------------------------------------------------------------------------
     Shared animation loop — single rAF for all active springs
     -------------------------------------------------------------------------- */
  const activeSets = new Set();
  let loopRunning = false;
  let lastTime = 0;

  function tick(now) {
    if (activeSets.size === 0) {
      loopRunning = false;
      return;
    }

    const dt = Math.min((now - lastTime) / 1000, 0.064); // cap at ~16fps minimum
    lastTime = now;

    for (const entry of activeSets) {
      entry.step(dt);
      if (entry.isAtRest()) {
        activeSets.delete(entry);
      }
    }

    requestAnimationFrame(tick);
  }

  function activate(entry) {
    activeSets.add(entry);
    if (!loopRunning) {
      loopRunning = true;
      lastTime = performance.now();
      requestAnimationFrame(tick);
    }
  }

  /* --------------------------------------------------------------------------
     SpringHover — bouncy scale on [data-spring-hover]
     -------------------------------------------------------------------------- */
  function initSpringHover() {
    if (REDUCED || IS_TOUCH) return;

    const els = document.querySelectorAll('[data-spring-hover]');
    for (const el of els) {
      const spring = new Spring({ stiffness: 300, damping: 15, mass: 0.8 });
      spring.value = 1;
      spring.target = 1;

      const entry = {
        step(dt) {
          spring.update(dt);
          el.style.transform = 'scale(' + spring.value.toFixed(4) + ')';
        },
        isAtRest() { return spring.isAtRest(0.0005); }
      };

      el.addEventListener('mouseenter', function () {
        spring.target = 1.05;
        activate(entry);
      });

      el.addEventListener('mouseleave', function () {
        spring.target = 1.0;
        activate(entry);
      });
    }
  }

  /* --------------------------------------------------------------------------
     SpringScroll — parallax with momentum on [data-spring-scroll]
     -------------------------------------------------------------------------- */
  function initSpringScroll() {
    if (REDUCED) return;

    const els = document.querySelectorAll('[data-spring-scroll]');
    if (els.length === 0) return;

    const items = [];
    const visibleItems = new Set();

    for (const el of els) {
      const speed = parseFloat(el.dataset.springScroll) || 0.15;
      const spring = new Spring({ stiffness: 60, damping: 12, mass: 1 });

      const entry = {
        el: el,
        spring: spring,
        speed: speed,
        cachedTop: 0,
        cachedHeight: 0,
        step(dt) {
          spring.update(dt);
          el.style.transform = 'translateY(' + spring.value.toFixed(2) + 'px)';
        },
        isAtRest() { return spring.isAtRest(0.05); }
      };

      items.push(entry);
    }

    function cacheRects() {
      const scrollY = window.scrollY;
      for (const item of items) {
        const rect = item.el.getBoundingClientRect();
        item.cachedTop = rect.top + scrollY;
        item.cachedHeight = rect.height;
      }
    }

    function onScroll() {
      const viewH = window.innerHeight;
      const scrollY = window.scrollY;

      for (const item of visibleItems) {
        const top = item.cachedTop - scrollY;
        const center = top + item.cachedHeight / 2;
        const offset = (center - viewH / 2) * item.speed;

        item.spring.target = offset;
        activate(item);
      }
    }

    const observer = new IntersectionObserver(function (entries) {
      for (const entry of entries) {
        const item = items.find(function (i) { return i.el === entry.target; });
        if (!item) continue;
        if (entry.isIntersecting) visibleItems.add(item);
        else visibleItems.delete(item);
      }
    }, { rootMargin: '100px 0px' });

    for (const item of items) observer.observe(item.el);

    cacheRects();
    window.addEventListener('resize', cacheRects, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* --------------------------------------------------------------------------
     SpringCards — 3D tilt with spring physics on [data-spring-tilt]
     -------------------------------------------------------------------------- */
  function initSpringCards() {
    if (REDUCED || IS_TOUCH) return;

    const els = document.querySelectorAll('[data-spring-tilt]');
    const MAX_DEG = 8;

    for (const el of els) {
      const springX = new Spring({ stiffness: 200, damping: 14, mass: 0.6 });
      const springY = new Spring({ stiffness: 200, damping: 14, mass: 0.6 });

      el.style.willChange = 'transform';

      /* Shine overlay for the tilt effect */
      const shine = el.querySelector('.spring-tilt-shine') || (function () {
        const s = document.createElement('div');
        s.className = 'spring-tilt-shine';
        s.style.cssText =
          'position:absolute;inset:0;pointer-events:none;z-index:1;' +
          'background:radial-gradient(circle at 50% 50%, rgba(200,148,62,0.15), transparent 60%);' +
          'opacity:0;transition:opacity 0.25s ease;';
        el.style.position = el.style.position || 'relative';
        el.style.overflow = 'hidden';
        el.appendChild(s);
        return s;
      })();

      let shineX = 50;
      let shineY = 50;

      const entry = {
        step(dt) {
          springX.update(dt);
          springY.update(dt);
          el.style.transform =
            'perspective(1000px) rotateX(' + springX.value.toFixed(3) +
            'deg) rotateY(' + springY.value.toFixed(3) + 'deg)';
        },
        isAtRest() { return springX.isAtRest(0.01) && springY.isAtRest(0.01); }
      };

      el.addEventListener('mousemove', function (e) {
        const rect = el.getBoundingClientRect();
        const nx = (e.clientX - rect.left) / rect.width;
        const ny = (e.clientY - rect.top) / rect.height;

        springX.target = (0.5 - ny) * MAX_DEG * 2;
        springY.target = (nx - 0.5) * MAX_DEG * 2;

        shineX = nx * 100;
        shineY = ny * 100;
        shine.style.background =
          'radial-gradient(circle at ' + shineX.toFixed(1) + '% ' +
          shineY.toFixed(1) + '%, rgba(200,148,62,0.18), transparent 60%)';
        shine.style.opacity = '1';

        activate(entry);
      });

      el.addEventListener('mouseleave', function () {
        springX.target = 0;
        springY.target = 0;
        shine.style.opacity = '0';
        activate(entry);
      });
    }
  }

  /* --------------------------------------------------------------------------
     Initialise all spring interactions
     -------------------------------------------------------------------------- */
  function init() {
    initSpringHover();
    initSpringScroll();
    initSpringCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Expose Spring class for magnetic-buttons.js and other scripts */
  window.BlackMangoSpring = {
    Spring: Spring,
    activate: activate
  };
})();
